import type { ReadonlyJSONValue } from "@rocicorp/zero";
import * as Sentry from "@sentry/react";
import { useToolOutput } from "../../hooks/useToolOutput";
import { useEffect, useRef } from "react";
import { Text } from "../Text";
import {
  type ChatTool,
  type ToolResult,
  toolResultFailed,
  type ToolUse as ToolUseData,
  type ToolUseProps,
} from "./ChatTool";
import {
  ActionHeader,
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedTimestamp,
} from "./Expand";
import { ChatDisclosure } from "./ChatDisclosure";
import { ToolStatus } from "./ToolStatus";
import { ActivityRow } from "./ActivityRow";

export function FallbackHeader({ toolUse, result, interrupted }: ToolUseProps) {
  const input = toolUse.input as Record<string, unknown> | null;
  const done = !!result;
  const text = done
    ? toolResultFailed(result)
      ? ((input?.failedDescription as string) ??
        `Failed to use ${toolUse.toolName}`)
      : ((input?.completedDescription as string) ??
        `Completed ${toolUse.toolName}`)
    : ((input?.activeDescription as string) ?? `Using ${toolUse.toolName}…`);
  return (
    <ToolStatus icon="tool-case" text={text} pending={!done && !interrupted} />
  );
}

function FallbackBody({ toolUse, result }: ToolUseProps) {
  const {
    activeDescription: _active,
    completedDescription: _completed,
    failedDescription: _failed,
    ...args
  } = (toolUse.input ?? {}) as Record<string, unknown>;
  return (
    <Text variant="code-small" color="var(--text-secondary)">
      <pre
        style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {JSON.stringify(args, null, 2)}
      </pre>
      {result && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {JSON.stringify(result.output, null, 2)}
        </pre>
      )}
    </Text>
  );
}

type Props = {
  toolUse: ToolUseData;
  result: ToolResult | null;
  toolAfter: boolean;
  connected?: boolean;
  expanded: boolean;
  toggleExpanded: () => void;
  tool: ChatTool | undefined;
  createdAt: number;
  completedAt?: number | null;
  interrupted?: boolean;
  onExpandHistory?: () => void;
  onProvideResult?: (
    entryId: string,
    output: unknown,
    isError: boolean
  ) => void;
};

export function ToolUse({
  toolUse,
  result,
  toolAfter,
  connected = true,
  expanded,
  toggleExpanded,
  tool,
  createdAt,
  completedAt,
  interrupted,
  onExpandHistory,
  onProvideResult,
}: Props) {
  const errorBoundaryRef = useRef<Sentry.ErrorBoundary>(null);
  const previousToolIdRef = useRef(toolUse.id);
  useEffect(() => {
    if (previousToolIdRef.current === toolUse.id) return;
    previousToolIdRef.current = toolUse.id;
    errorBoundaryRef.current?.resetErrorBoundary();
  }, [toolUse.id]);

  // Output is stored only on completion, so a fetch while pending would find nothing and never retry.
  const shouldLoad =
    !!result &&
    !!(
      expanded ||
      (!onExpandHistory && tool?.alwaysExpand) ||
      tool?.alwaysLoadOutput
    );

  const { output: lazyOutput } = useToolOutput(toolUse.id, shouldLoad);

  const effectiveResult: ToolResult | null = result
    ? {
        ...result,
        output: (lazyOutput as ReadonlyJSONValue) ?? result.output,
      }
    : null;

  const Header = tool?.Header ?? FallbackHeader;
  const Body = tool?.Body ?? FallbackBody;
  const data = tool?.data;
  const activate = onExpandHistory ? undefined : tool?.onActivate;
  const hasContent =
    result !== null ||
    tool?.alwaysExpand ||
    tool?.expandableWhilePending ||
    !tool;

  const expandable = !!onExpandHistory || hasContent;
  const isExpanded = !onExpandHistory && (expanded || !!tool?.alwaysExpand);

  const body = (
    <Body
      key={toolUse.id}
      toolUse={toolUse}
      result={effectiveResult}
      createdAt={createdAt}
      completedAt={completedAt}
      data={data}
      interrupted={!!interrupted}
      expanded={isExpanded}
      onProvideResult={onProvideResult}
    />
  );

  const header = (
    <Header
      toolUse={toolUse}
      result={effectiveResult}
      createdAt={createdAt}
      data={data}
      interrupted={!!interrupted}
      expanded={isExpanded}
    />
  );

  return (
    <Sentry.ErrorBoundary
      ref={errorBoundaryRef}
      fallback={({ error }) => (
        <ActivityRow connected={connected} hasNext={toolAfter}>
          <CollapsibleHeader
            disabled={!onExpandHistory}
            aria-expanded={onExpandHistory ? false : undefined}
            onPress={() => onExpandHistory?.()}
          >
            <CollapsibleHeaderInner>
              <ToolStatus
                icon="circle-minus"
                iconColor="var(--red-600)"
                text={`Failed to render ${toolUse.toolName}`}
              />
            </CollapsibleHeaderInner>
          </CollapsibleHeader>
          <Text
            variant="code-small"
            color="var(--text-tertiary)"
            style={{ padding: "2px 6px 8px 6px" }}
          >
            {error instanceof Error ? error.message : String(error)}
          </Text>
        </ActivityRow>
      )}
    >
      <ActivityRow connected={connected} hasNext={toolAfter}>
        {activate ? (
          <ActionHeader
            disabled={!expandable}
            onClick={() =>
              activate({
                toolUse,
                result: effectiveResult,
                createdAt,
                data,
                interrupted: !!interrupted,
                onProvideResult,
              })
            }
          >
            <CollapsibleHeaderInner>{header}</CollapsibleHeaderInner>
          </ActionHeader>
        ) : onExpandHistory ? (
          <CollapsibleHeader aria-expanded={false} onPress={onExpandHistory}>
            <CollapsibleHeaderInner>{header}</CollapsibleHeaderInner>
          </CollapsibleHeader>
        ) : (
          <ChatDisclosure
            header={header}
            disabled={!expandable}
            expanded={!!isExpanded}
            onToggle={toggleExpanded}
            content={tool?.expandableWhilePending ? "plain" : "panel"}
            keepMounted
            footer={
              <ExpandedTimestamp
                createdAt={createdAt}
                durationMs={
                  completedAt != null ? completedAt - createdAt : null
                }
              />
            }
          >
            {body}
          </ChatDisclosure>
        )}
      </ActivityRow>
    </Sentry.ErrorBoundary>
  );
}
